import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import { TilloBrand } from '../../core/models/api.models';

@Component({
  selector: 'app-tillo-brands',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './tillo-brands.component.html',
  styleUrl: './tillo-brands.component.scss',
})
export class TilloBrandsComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly savingId = signal<number | null>(null);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<TilloBrand[]>([]);
  readonly page = signal(1);
  readonly perPage = 20;
  readonly total = signal(0);
  readonly pages = signal(1);
  readonly drafts = signal<Record<number, string>>({});

  searchName = '';

  ngOnInit(): void {
    this.load();
  }

  load(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    this.api.getTilloBrands(page, this.perPage, this.searchName).subscribe({
      next: (data) => {
        const rows = data?.rows || [];
        this.rows.set(rows);
        this.total.set(data?.pagination?.count || 0);
        this.pages.set(Math.max(1, data?.pagination?.pages || 1));
        this.page.set(data?.pagination?.page || page);
        const drafts: Record<number, string> = {};
        for (const row of rows) {
          drafts[row.id] = String(row.points_per_dollar ?? 1);
        }
        this.drafts.set(drafts);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load Tillo brands');
        this.loading.set(false);
      },
    });
  }

  search(): void {
    this.load(1);
  }

  prev(): void {
    if (this.page() > 1) this.load(this.page() - 1);
  }

  next(): void {
    if (this.page() < this.pages()) this.load(this.page() + 1);
  }

  onDraftChange(id: number, value: string | number): void {
    this.drafts.update((current) => ({ ...current, [id]: String(value) }));
  }

  isDirty(row: TilloBrand): boolean {
    const draft = parseFloat(this.drafts()[row.id]);
    const saved = parseFloat(String(row.points_per_dollar ?? 1));
    if (!Number.isFinite(draft) || !Number.isFinite(saved)) return true;
    return draft !== saved;
  }

  save(row: TilloBrand): void {
    if (this.savingId()) return;
    const raw = this.drafts()[row.id];
    const value = parseFloat(raw);
    if (!Number.isFinite(value) || value < 0) {
      this.error.set('Points per dollar must be a number of 0 or more');
      return;
    }

    this.savingId.set(row.id);
    this.error.set(null);
    this.success.set(null);
    this.api.updateTilloBrand(row.id, { points_per_dollar: value }).subscribe({
      next: (updated) => {
        this.rows.update((rows) =>
          rows.map((item) =>
            item.id === row.id
              ? { ...item, points_per_dollar: updated?.points_per_dollar ?? value }
              : item
          )
        );
        this.drafts.update((current) => ({
          ...current,
          [row.id]: String(updated?.points_per_dollar ?? value),
        }));
        this.savingId.set(null);
        this.success.set(`Updated ${row.name || 'brand'} to ${value} points per dollar`);
      },
      error: (err: { message?: string }) => {
        this.savingId.set(null);
        this.error.set(err?.message || 'Failed to update points per dollar');
      },
    });
  }

  sync(): void {
    if (this.syncing()) return;
    this.syncing.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.syncTilloBrands().subscribe({
      next: (result) => {
        this.syncing.set(false);
        this.success.set(
          `Synced Tillo brands: fetched ${result.fetched}, created ${result.created ?? 0}, updated ${result.updated ?? 0}`
        );
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.syncing.set(false);
        this.error.set(err?.message || 'Failed to sync Tillo brands');
      },
    });
  }
}
